import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { WorkOrder, formatWorkOrderNumber } from "@/hooks/use-work-orders";
import { MaintenancePlan } from "@/hooks/use-maintenance-plans";
import { FleetProvider } from "@/hooks/use-fleet-providers";
import { parseBrazilianCurrency, formatBrazilianNumber } from "@/lib/utils";
import { WorkOrderItemsEditor } from "./WorkOrderItemsEditor";
import { LocalWorkOrderItem, createLocalItem, calculateTotalFromItems, useWorkOrderItems, useCreateWorkOrderItem, useDeleteWorkOrderItem } from "@/hooks/use-work-order-items";
import { supabase } from "@/integrations/supabase/client";
import { useUserCompanies } from "@/hooks/useUserCompanies";

interface WorkOrderFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  workOrder?: WorkOrder | null;
  vehicles: Array<{ id: string; plate: string; make?: string; model?: string }>;
  maintenancePlans: MaintenancePlan[];
  providers: FleetProvider[];
  isLoading?: boolean;
  preSelectedPlanId?: string;
  preSelectedVehicleId?: string;
}

export function WorkOrderFormSheet({
  open,
  onOpenChange,
  onSubmit,
  workOrder,
  vehicles,
  maintenancePlans,
  providers,
  isLoading = false,
  preSelectedPlanId,
  preSelectedVehicleId
}: WorkOrderFormSheetProps) {
  const { data: userCompanies } = useUserCompanies();
  const companyId = userCompanies?.[0]?.company_id || '';
  
  const [formData, setFormData] = useState({
    vehicle_id: "",
    plan_id: "",
    provider_id: "",
    description: "",
    status: "open",
    total_cost: "",
    opened_at: new Date().toISOString().split('T')[0]
  });

  // Estado local dos itens (para nova OS)
  const [localItems, setLocalItems] = useState<LocalWorkOrderItem[]>([]);
  
  // Carregar itens existentes se estiver editando
  const { data: existingItems } = useWorkOrderItems(workOrder?.id);
  const createItem = useCreateWorkOrderItem(companyId);
  const deleteItem = useDeleteWorkOrderItem(companyId);

  // Quando abre para editar, carregar itens existentes como LocalWorkOrderItem
  useEffect(() => {
    if (workOrder && existingItems) {
      setLocalItems(existingItems.map(item => ({
        tempId: item.id, // Usar ID real como tempId para itens existentes
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      })));
    } else if (!workOrder) {
      setLocalItems([]);
    }
  }, [workOrder, existingItems, open]);

  useEffect(() => {
    if (workOrder) {
      setFormData({
        vehicle_id: workOrder.vehicle_id,
        plan_id: workOrder.plan_id || "none",
        provider_id: workOrder.provider_id || "none",
        description: workOrder.description || "",
        status: workOrder.status,
        total_cost: workOrder.total_cost ? formatBrazilianNumber(workOrder.total_cost) : "",
        opened_at: workOrder.opened_at ? workOrder.opened_at.split('T')[0] : ""
      });
    } else {
      setFormData({
        vehicle_id: preSelectedVehicleId || "",
        plan_id: preSelectedPlanId || "none",
        provider_id: "none",
        description: "",
        status: "open",
        total_cost: "",
        opened_at: new Date().toISOString().split('T')[0]
      });
    }
  }, [workOrder, open, preSelectedPlanId, preSelectedVehicleId]);

  // Atualizar total_cost quando os itens mudarem
  useEffect(() => {
    if (localItems.length > 0) {
      const total = calculateTotalFromItems(localItems);
      setFormData(prev => ({
        ...prev,
        total_cost: formatBrazilianNumber(total)
      }));
    }
  }, [localItems]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Calcular total final (dos itens se houver, senão do campo manual)
    const totalFromItems = localItems.length > 0 ? calculateTotalFromItems(localItems) : parseBrazilianCurrency(formData.total_cost);
    
    const submitData = {
      ...formData,
      total_cost: totalFromItems,
      plan_id: formData.plan_id === "none" ? null : formData.plan_id || null,
      provider_id: formData.provider_id === "none" ? null : formData.provider_id || null,
      opened_at: formData.opened_at ? new Date(formData.opened_at).toISOString() : new Date().toISOString(),
      // Passar itens para serem salvos após criar/atualizar a OS
      _items: localItems
    };

    if (workOrder) {
      onSubmit({ id: workOrder.id, ...submitData });
    } else {
      onSubmit(submitData);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Se veio de um plano ou está editando uma OS existente, travar veículo e plano
  const isLockedToPlan = Boolean(preSelectedPlanId) || Boolean(workOrder);
  const selectedVehicle = vehicles.find(v => v.id === formData.vehicle_id);
  const selectedPlan = maintenancePlans.find(p => p.id === formData.plan_id);

  // Desabilitar campo de custo manual se houver itens
  const hasCostFromItems = localItems.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[700px] p-6 overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {workOrder 
              ? `Editar Ordem de Serviço ${workOrder.order_number ? formatWorkOrderNumber(workOrder.order_number) : ''}`
              : "Nova Ordem de Serviço"
            }
          </SheetTitle>
          <SheetDescription>
            Registre uma ordem de serviço para manutenção do veículo.
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <form id="work-order-form" onSubmit={handleSubmit} className="space-y-4 py-4">
            {/* Veículo e Plano - travados quando vem de um plano */}
            {isLockedToPlan ? (
              <div className="space-y-3 p-4 rounded-lg bg-muted/50 border">
                <div>
                  <Label className="text-xs text-muted-foreground">Veículo</Label>
                  <p className="font-medium">
                    {selectedVehicle ? `${selectedVehicle.plate} - ${selectedVehicle.make} ${selectedVehicle.model}` : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Plano de Manutenção</Label>
                  <p className="font-medium">
                    {selectedPlan ? `${selectedPlan.name} - ${selectedPlan.type}` : 'N/A'}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_id">Veículo *</Label>
                  <Select value={formData.vehicle_id} onValueChange={(value) => handleChange("vehicle_id", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o veículo" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.plate} - {vehicle.make} {vehicle.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="plan_id">Plano de Manutenção</Label>
                  <Select value={formData.plan_id} onValueChange={(value) => handleChange("plan_id", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um plano (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {maintenancePlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name} - {plan.type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="provider_id">Fornecedor</Label>
              <Select value={formData.provider_id} onValueChange={(value) => handleChange("provider_id", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um fornecedor (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {providers.map((provider: any) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição do Serviço *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Descreva o serviço a ser realizado..."
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Aberto</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="closed">Fechado</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_cost">Custo Total (R$)</Label>
                <Input
                  id="total_cost"
                  type="text"
                  value={formData.total_cost}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Permite apenas números, vírgula e ponto
                    if (/^[\d.,]*$/.test(inputValue)) {
                      handleChange("total_cost", inputValue);
                    }
                  }}
                  placeholder="0,00"
                  disabled={hasCostFromItems}
                  className={hasCostFromItems ? "bg-muted" : ""}
                />
                {hasCostFromItems && (
                  <p className="text-xs text-muted-foreground">
                    Calculado automaticamente dos itens
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="opened_at">Data de Abertura</Label>
                <Input
                  id="opened_at"
                  type="date"
                  value={formData.opened_at}
                  onChange={(e) => handleChange("opened_at", e.target.value)}
                />
              </div>
            </div>

            <Separator className="my-4" />

            {/* Seção de Itens Detalhados */}
            <WorkOrderItemsEditor
              items={localItems}
              onChange={setLocalItems}
              disabled={isLoading}
            />
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="work-order-form" disabled={isLoading}>
            {isLoading ? "Salvando..." : workOrder ? "Atualizar" : "Criar"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
